import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { Quote } from "lucide-react";

/**
 * Displays a daily inspirational quote. The quote:
 *   - rotates on every new login (AuthContext clears the cache on login/logout);
 *   - rotates on every new calendar day even during a long-lived session;
 *   - stays the same across simple page reloads within the same day + session
 *     so the user isn't jarred by a different quote when navigating.
 *
 * Cache format (localStorage key `daily_quote_<userId>`):
 *   { date: "YYYY-MM-DD", quote: <full payload> }
 *
 * When `blurred` is true the quote text is visually obscured — used as the
 * backdrop behind the login-check-in modal.
 */
export default function DailyQuoteCard({ blurred = false, onLoaded }) {
  const { t } = useT();
  const { user } = useAuth();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const today = new Date().toISOString().slice(0, 10);
    const cacheKey = user?.id ? `daily_quote_${user.id}` : null;

    // 1) Try the local cache first — same day + same session → same quote.
    let cached = null;
    try {
      if (cacheKey) {
        const raw = localStorage.getItem(cacheKey);
        if (raw) cached = JSON.parse(raw);
      }
    } catch {
      cached = null;
    }
    if (cached && cached.date === today && cached.quote) {
      setQuote(cached.quote);
      onLoaded?.(cached.quote);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    // 2) Otherwise fetch a new random quote, avoiding the last one shown.
    (async () => {
      try {
        const params = {};
        if (cached?.quote?.index != null) params.exclude_index = cached.quote.index;
        const { data } = await api.get("/quotes/today", { params });
        if (cancelled) return;
        setQuote(data);
        onLoaded?.(data);
        try {
          if (cacheKey) {
            localStorage.setItem(cacheKey, JSON.stringify({ date: today, quote: data }));
          }
        } catch {
          // localStorage may be blocked (iframe) — silently skip caching.
        }
      } catch {
        // Silent — the quote is a nice-to-have, don't block the page.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  if (loading) {
    return (
      <div
        className="bg-white border border-[#E5E2DC] rounded-2xl p-6 sm:p-8"
        data-testid="daily-quote-loading"
      >
        <p className="text-xs uppercase tracking-[0.2em] text-[#8A9188] font-bold">
          {t("quote.loading")}
        </p>
      </div>
    );
  }

  if (!quote) return null;

  return (
    <div
      className={`relative bg-[#F2F0EA] border border-[#E5E2DC] rounded-2xl p-6 sm:p-10 overflow-hidden ${
        blurred ? "select-none" : ""
      }`}
      data-testid="daily-quote-card"
      aria-hidden={blurred || undefined}
    >
      <div
        className={
          blurred
            ? "blur-md pointer-events-none transition-[filter] duration-500"
            : "transition-[filter] duration-500"
        }
      >
        <div className="flex items-start gap-4">
          <div className="h-11 w-11 rounded-xl bg-white flex items-center justify-center shrink-0 border border-[#E5E2DC]">
            <Quote strokeWidth={1.5} className="text-[#B37A5F]" size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9188]">
              {t("quote.overline")}
            </p>
            <blockquote
              className="mt-3 font-heading text-2xl sm:text-3xl text-[#1A1D1A] tracking-tight font-light leading-snug italic"
              data-testid="daily-quote-text"
            >
              &ldquo;{quote.quote}&rdquo;
            </blockquote>
            {quote.explanation && (
              <p
                className="mt-5 text-sm sm:text-base text-[#2A2D2A] leading-relaxed"
                data-testid="daily-quote-explanation"
              >
                {quote.explanation}
              </p>
            )}
            <div className="mt-6 pt-4 border-t border-[#E5E2DC] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm">
              <p
                className="font-semibold text-[#1A1D1A]"
                data-testid="daily-quote-author"
              >
                {quote.author}
              </p>
              {quote.source && (
                <p
                  className="text-[#5C635A] italic"
                  data-testid="daily-quote-source"
                >
                  {t("quote.source_label")}: {quote.source}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
